import { useEffect, useLayoutEffect, useRef, useState } from "react";

interface UseAutoTableHeightOptions {
  minHeight?: number;
  debounceMs?: number;
}

export function useAutoTableHeight(options?: UseAutoTableHeightOptions) {
  const { minHeight = 0, debounceMs = 0 } = options ?? {};

  const containerRef = useRef<HTMLDivElement>(null);
  const toolbarRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLDivElement>(null);
  const pagingRef = useRef<HTMLDivElement>(null);

  const [tableHeight, setTableHeight] = useState<number>(0);

  const calcHeight = () => {
    if (!containerRef.current) return;

    const containerHeight = containerRef.current.clientHeight;
    const toolbarHeight = toolbarRef.current?.offsetHeight ?? 0;
    const pagingHeight = pagingRef.current?.offsetHeight ?? 0;

    const result = containerHeight - toolbarHeight - pagingHeight;

    setTableHeight(Math.max(result, minHeight));
  };

  useLayoutEffect(() => {
    calcHeight();
  }, []);

  useEffect(() => {
    if (!containerRef.current) return;

    let timer: any;

    const handler = () => {
      if (debounceMs > 0) {
        clearTimeout(timer);
        timer = setTimeout(calcHeight, debounceMs);
      } else {
        calcHeight();
      }
    };

    const observer = new ResizeObserver(handler);

    observer.observe(containerRef.current);
    if (toolbarRef.current) observer.observe(toolbarRef.current);
    if (pagingRef.current) observer.observe(pagingRef.current);

    window.addEventListener("resize", handler);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", handler);
      clearTimeout(timer);
    };
  }, [debounceMs, minHeight]);

  return {
    containerRef,
    toolbarRef,
    tableRef,
    pagingRef,
    tableHeight,
  };
}
